import { 
  updateUserPreferredNameAction,
  getUserPreferredNameAction 
} from './server/preferred-name/PreferedNameAction';
import { 
  updateUserLanguageAction,
  getUserLanguageAction 
} from './server/language/LanguageAction';
import { initializeUserDataFromDBAction } from './server/init-user-app-data/InitUserAppDataAction';
import { 
  addUserCreditsByUserIdActionSQL,
  decrementUserCreditCountActionSQL,
  getBothUserCreditCountActionSQL
} from '@/app/subscription/user-credit/server/credit_actions';
import { prewarmIfAllowed } from '@/app/lib/actions/room-gpu-match-actions';

/**
 * Interface for user location data
 */
export interface UserLocationData {
  city: string;
  country: string;
  region?: string;
  timezone?: string;
  postalCode?: string;
}

/**
 * Interface for user app data
 */
export interface UserAppData {
  userEmail: string;
  userId: string;
  userPreferredName: string;
  creditCount: number;
  dailyCreditCount: number;
  language: string | null;
  userLocation?: UserLocationData;
}

/**
 * Callback type for state change notifications
 */
export type UserDataChangeCallback = () => void;

/**
 * UserAppData singleton manages all user-related data centrally
 */
export class UserAppDataSingleton {
  private static instance: UserAppDataSingleton;
  
  // User data
  private userData: UserAppData = {
    userEmail: '',
    userId: '',
    userPreferredName: '',
    creditCount: 0,
    dailyCreditCount: 0,
    language: null,
    userLocation: undefined
  };
  
  // Subscribers for state changes
  private subscribers: Set<UserDataChangeCallback> = new Set();
  
  private constructor() {}
  
  /**
   * Get singleton instance
   */
  public static getInstance(): UserAppDataSingleton {
    if (!UserAppDataSingleton.instance) {
      UserAppDataSingleton.instance = new UserAppDataSingleton();
    }
    return UserAppDataSingleton.instance;
  }
  
  // =============================================================================
  // USER EMAIL METHODS (READ ONLY)
  // =============================================================================
  
  /**
   * Get user email (read only)
   */
  public getUserEmail(): string {
    return this.userData.userEmail;
  }
  
  /**
   * Set user email (internal use only - typically set during initialization)
   */
  private setUserEmail(email: string): void {
    if (this.userData.userEmail !== email) {
      this.userData.userEmail = email;
      this.notifySubscribers();
    }
  }
  
  // =============================================================================
  // USER ID METHODS
  // =============================================================================
  
  /**
   * Get user ID
   */
  public getUserId(): string {
    return this.userData.userId;
  }
  
  /**
   * Set user ID
   */
  public setUserId(userId: string): void {
    if (this.userData.userId !== userId) {
      this.userData.userId = userId;
      this.notifySubscribers();
    }
  }
  
  // =============================================================================
  // USER PREFERRED NAME METHODS
  // =============================================================================
  
  /**
   * Get user preferred name
   */
  public getUserPreferredName(): string {
    return this.userData.userPreferredName;
  }
  
  /**
   * Set user preferred name (local only - no database write)
   */
  public setUserPreferredName(preferredName: string): void {
    if (this.userData.userPreferredName !== preferredName) {
      this.userData.userPreferredName = preferredName;
      this.notifySubscribers();
    }
  }

  /**
   * Load user preferred name from database
   */
  public async loadUserPreferredNameFromDB(): Promise<void> {
    if (!this.userData.userEmail) {
      console.warn('Cannot load preferred name: user email not set');
      return;
    }

    try {
      const result = await getUserPreferredNameAction(this.userData.userEmail);
      
      if (result.success && result.preferredName) {
        if (this.userData.userPreferredName !== result.preferredName) {
          this.userData.userPreferredName = result.preferredName;
          this.notifySubscribers();
        }
      } else {
        console.warn('Failed to load preferred name:', result.message);
      }
    } catch (error) {
      console.error('Error loading preferred name from DB:', error);
    }
  }
  
  /**
   * Set user preferred name and write to database
   */
  public async setUserPreferredNameAndWriteToDB(preferredName: string): Promise<boolean> {
    if (!this.userData.userEmail) {
      console.warn('Cannot update preferred name: user email not set');
      return false;
    }

    try {
      // First, write to database
      const result = await updateUserPreferredNameAction(this.userData.userEmail, preferredName);
      
      if (result.success) {
        // Only update local state if database write was successful
        if (this.userData.userPreferredName !== preferredName) {
          this.userData.userPreferredName = preferredName;
          this.notifySubscribers();
        }
        return true;
      } else {
        console.error('Failed to update preferred name in DB:', result.message);
        return false;
      }
    } catch (error) {
      console.error('Error updating preferred name:', error);
      return false;
    }
  }
  
  // =============================================================================
  // Credit COUNT METHODS
  // =============================================================================
  
  /**
   * Get credit count
   */
  public getCreditCount(): number {
    return this.userData.creditCount;
  }
  
  /**
   * Get daily credit count
   */
  public getDailyCreditCount(): number {
    return this.userData.dailyCreditCount;
  }

  // =============================================================================
  // LANGUAGE METHODS
  // =============================================================================

  /**
   * Get user language preference
   */
  public getLanguage(): string | null {
    return this.userData.language;
  }

  /**
   * Set user language preference (local only - no database write)
   */
  public setLanguage(language: string | null): void {
    if (this.userData.language !== language) {
      this.userData.language = language;
      this.notifySubscribers();
    }
  }

  /**
   * Load user language preference from database
   */
  public async loadUserLanguageFromDB(): Promise<void> {
    if (!this.userData.userEmail) {
      console.warn('Cannot load language: user email not set');
      return;
    }

    try {
      const result = await getUserLanguageAction(this.userData.userEmail);
      
      if (result.success && result.language !== null) {
        if (this.userData.language !== result.language) {
          this.userData.language = result.language;
          this.notifySubscribers();
        }
      } else if (result.success && result.language === null) {
        // Language is null in DB, update local state
        if (this.userData.language !== null) {
          this.userData.language = null;
          this.notifySubscribers();
        }
      } else {
        console.warn('Failed to load language:', result.message);
      }
    } catch (error) {
      console.error('Error loading language from DB:', error);
    }
  }
  
  /**
   * Set user language preference and write to database
   */
  public async setLanguageAndWriteToDB(language: string | null): Promise<boolean> {
    if (!this.userData.userEmail) {
      console.warn('Cannot update language: user email not set');
      return false;
    }

    try {
      // First, write to database
      const result = await updateUserLanguageAction(this.userData.userEmail, language);
      
      if (result.success) {
        // Only update local state if database write was successful
        if (this.userData.language !== language) {
          this.userData.language = language;
          this.notifySubscribers();
        }
        return true;
      } else {
        console.error('Failed to update language in DB:', result.message);
        return false;
      }
    } catch (error) {
      console.error('Error updating language:', error);
      return false;
    }
  }

  // =============================================================================
  // USER LOCATION METHODS
  // =============================================================================

  /**
   * Get user location data
   */
  public getUserLocation(): UserLocationData | undefined {
    return this.userData.userLocation;
  }

  /**
   * Set user location data
   */
  public setUserLocation(location: UserLocationData | undefined): void {
    if (JSON.stringify(this.userData.userLocation) !== JSON.stringify(location)) {
      this.userData.userLocation = location;
      this.notifySubscribers();
    }
  }

  /**
   * Update user location from Vercel headers
   * This method can be called from API routes or server components
   */
  public updateUserLocationFromHeaders(headers: {
    get: (key: string) => string | null;
  }): void {
    const country = headers.get('X-User-Country');
    const region = headers.get('X-User-Region');
    const city = headers.get('X-User-City');
    const timezone = headers.get('X-User-Timezone');
    const postalCode = headers.get('X-User-Postal-Code');

    // Only update if we have at least city and country
    if (city && country) {
      const locationData: UserLocationData = {
        city,
        country,
        region: region || undefined,
        timezone: timezone || undefined,
        postalCode: postalCode || undefined
      };

      this.setUserLocation(locationData);
    }
  }

  /**
   * Add credits to current count
   */
  public async addCredits(amount: number, dailyAmount: number = 0): Promise<{success: boolean, newCount: number, message: string}> {
    if (!this.userData.userId) {
      return { success: false, newCount: this.userData.creditCount, message: 'User ID not available' };
    }

    try {
      const result = await addUserCreditsByUserIdActionSQL(this.userData.userId, amount, dailyAmount);
      
      if (result.success) {
        // Update both credit counts directly from the result
        this.userData.creditCount = result.credit;
        this.userData.dailyCreditCount = result.dailyCredit;
        this.notifySubscribers();
      }
      
      return { 
        success: result.success, 
        newCount: result.credit, // Return permanent credit count for backward compatibility
        message: result.message 
      };
    } catch (error) {
      console.error('Error adding credits:', error);
      return { success: false, newCount: this.userData.creditCount, message: 'Error adding credits' };
    }
  }
  
  /**
   * Subtract credits from current count
   * Logic: First subtract from daily credits, then from permanent credits if needed
   */
  public async subtractCredits(amount: number): Promise<{success: boolean, newCount: number, message: string}> {
    if (!this.userData.userEmail) {
      return { success: false, newCount: this.userData.creditCount, message: 'User email not available' };
    }
    if (amount <= 0) {
      return { success: true, newCount: this.userData.creditCount, message: 'Free operation' };
    }

    try {
      // Calculate how much to subtract from daily vs permanent credits
      const dailyAvailable = this.userData.dailyCreditCount;
      let dailyToSubtract = 0;
      let permanentToSubtract = 0;

      if (dailyAvailable >= amount) {
        // Daily credits can cover the full amount
        dailyToSubtract = amount;
        permanentToSubtract = 0;
      } else {
        // Use all daily credits and subtract the rest from permanent
        dailyToSubtract = dailyAvailable;
        permanentToSubtract = amount - dailyAvailable;
      }

      const result = await decrementUserCreditCountActionSQL(this.userData.userEmail, permanentToSubtract, dailyToSubtract);
      
      if (result.success) {
        // After subtracting credits, fetch the updated values to ensure accuracy
        const updatedCounts = await getBothUserCreditCountActionSQL(this.userData.userEmail);
        if (updatedCounts.success) {
          this.userData.creditCount = updatedCounts.credit;
          this.userData.dailyCreditCount = updatedCounts.dailyCredit;
        }
        this.notifySubscribers();
      }
      
      return result;
    } catch (error) {
      console.error('Error subtracting credits:', error);
      return { success: false, newCount: this.userData.creditCount, message: 'Error subtracting credits' };
    }
  }
  
  // =============================================================================
  // BULK DATA METHODS
  // =============================================================================
  
  /**
   * Get all user data
   */
  public getAllUserData(): UserAppData {
    return { ...this.userData };
  }
  
  /**
   * Initialize user data from database (typically called during app startup)
   * Loads all user data (ID, preferred name, credit count) from database in one call
   * Also initiates prewarm if user is not already prewarmed
   */
  public async initializeUserDataFromDB(userEmail: string): Promise<boolean> {
    try {
      const result = await initializeUserDataFromDBAction(userEmail);
      
      if (result.success && result.userData) {
        this.userData = { ...result.userData };
        this.notifySubscribers();

        // Initiate prewarm if user is not already prewarmed
        try {
          // prewarmIfAllowed(userEmail);
        } catch (error) {
          console.error('Error initiating prewarm during user data initialization:', error);
          // Don't fail the initialization if prewarm fails
        }

        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error initializing user data from DB:', error);
      return false;
    }
  }

  /**
   * Initialize user data (legacy method - kept for backward compatibility)
   * @deprecated Use initializeUserDataFromDB instead
   */
  public async initializeUserData(userData: UserAppData): Promise<void> {
    this.userData = { ...userData };
    
    // Load preferred name from database if user email is available
    if (this.userData.userEmail) {
      await this.loadUserPreferredNameFromDB();
    }
    
    this.notifySubscribers();
  }
  
  // =============================================================================
  // SUBSCRIPTION METHODS
  // =============================================================================
  
  /**
   * Subscribe to state changes
   */
  public subscribe(callback: UserDataChangeCallback): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }
  
  /**
   * Notify all subscribers of state changes
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback());
  }
  
  // =============================================================================
  // UTILITY METHODS
  // =============================================================================
  
  /**
   * Reset all user data (useful for logout or testing)
   */
  public reset(): void {
    this.userData = {
      userEmail: '',
      userId: '',
      userPreferredName: '',
      creditCount: 0,
      dailyCreditCount: 0,
      language: null,
      userLocation: undefined
    };
    this.notifySubscribers();
  }
  
  /**
   * Check if user data is initialized
   */
  public isInitialized(): boolean {
    return this.userData.userEmail !== '' && this.userData.userId !== '';
  }

  /**
   * Check if user data is initialized for a specific user email
   */
  public isInitializedForUser(userEmail: string): boolean {
    return this.isInitialized() && this.userData.userEmail === userEmail;
  }
}

// Export singleton instance
export const userAppData = UserAppDataSingleton.getInstance();